import { useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Link } from "react-router-dom"
import { apiFetch } from "@/hooks/apiFetch"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"
import {
  ReportBranchFilter,
  ReportBranchProvider,
  useReportBranch,
} from "@/components/Reports/ReportBranchFilter"

const blankStaff = {
  name: "",
  phone: "",
  position: "",
  notes: "",
  is_active: true,
}

export default function StaffPage() {
  return (
    <ReportBranchProvider>
      <StaffRegistry />
    </ReportBranchProvider>
  )
}

function StaffRegistry() {
  const permissions = new Set(
    useAppSelector((state) => state.auth.user?.permissions ?? [])
  )
  // The company admin portal reads every branch's staff; they are managed
  // on the branch portals.
  const adminPortal = isAdminPortal()
  const canChange = (permission) => !adminPortal && permissions.has(permission)
  const { branchId } = useReportBranch()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(null)
  const params = new URLSearchParams()
  if (search) params.set("search", search)
  if (branchId) params.set("branch_id", branchId)
  const { data: staffResponse, isLoading } = useCustomQuery(
    ["hr-staff", search, branchId],
    `staff?${params.toString()}`
  )
  const staff = staffResponse?.data ?? []

  const save = useMutation({
    mutationFn: () =>
      apiFetch(editingId ? `staff/${editingId}` : "staff", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      toast.success(editingId ? "Staff member updated" : "Staff member added")
      setForm(null)
      queryClient.invalidateQueries({ queryKey: ["hr-staff"] })
      queryClient.invalidateQueries({ queryKey: ["hr-attendance"] })
    },
    onError: (error) =>
      toast.error(error?.message ?? "Unable to save staff member"),
  })

  const archive = useMutation({
    mutationFn: (id) => apiFetch(`staff/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Staff member archived")
      queryClient.invalidateQueries({ queryKey: ["hr-staff"] })
      queryClient.invalidateQueries({ queryKey: ["hr-attendance"] })
    },
    onError: (error) =>
      toast.error(error?.message ?? "Unable to archive staff member"),
  })

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...blankStaff })
  }
  const openEdit = (member) => {
    setEditingId(member.id)
    setForm({
      name: member.name,
      phone: member.phone ?? "",
      position: member.position ?? "",
      notes: member.notes ?? "",
      is_active: member.is_active,
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h5">Staff</Typography>
          <Typography color="text.secondary">
            HR records for attendance. These staff members do not have POS login
            accounts.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          {permissions.has("hr-attendance-view") && (
            <Button component={Link} to="/pos/attendance" variant="outlined">
              Attendance
            </Button>
          )}
          {canChange("hr-staff_members-create") && (
            <Button variant="contained" onClick={openCreate}>
              Add staff
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
        <TextField
          size="small"
          label="Search staff"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <ReportBranchFilter sx={{ mb: 0 }} />
      </Stack>

      <Paper variant="outlined">
        {isLoading ? (
          <Typography sx={{ p: 2 }}>Loading staff...</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {adminPortal && <TableCell>Branch</TableCell>}
                <TableCell>Position</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.name}</TableCell>
                  {adminPortal && (
                    <TableCell>{member.branch_name || "—"}</TableCell>
                  )}
                  <TableCell>{member.position || "—"}</TableCell>
                  <TableCell>{member.phone || "—"}</TableCell>
                  <TableCell>
                    {member.is_active ? "Active" : "Inactive"}
                  </TableCell>
                  <TableCell align="right">
                    {canChange("hr-staff_members-update") && (
                      <Button size="small" onClick={() => openEdit(member)}>
                        Edit
                      </Button>
                    )}
                    {canChange("hr-staff_members-delete") && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Archive ${member.name}? Their attendance history will remain.`
                            )
                          ) {
                            archive.mutate(member.id)
                          }
                        }}
                      >
                        Archive
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {staff.length === 0 && (
                <TableRow>
                  <TableCell colSpan={adminPortal ? 6 : 5}>
                    No staff members found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingId ? "Edit staff member" : "Add staff member"}
        </DialogTitle>
        {form && (
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <DialogContent>
              <Stack gap={2} sx={{ pt: 1 }}>
                <TextField
                  required
                  label="Name"
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
                <TextField
                  label="Position"
                  value={form.position}
                  onChange={(event) =>
                    setForm({ ...form, position: event.target.value })
                  }
                />
                <TextField
                  label="Phone"
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                />
                <TextField
                  label="Notes"
                  multiline
                  minRows={2}
                  value={form.notes}
                  onChange={(event) =>
                    setForm({ ...form, notes: event.target.value })
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(event) =>
                        setForm({ ...form, is_active: event.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setForm(null)}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={save.isPending || !form.name.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Box>
  )
}
